import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { formatCurrency, toBaseCurrency, getCurrencySymbol, API_URL } from "../lib/utils";
import { CashSessionPanel } from "../components/cash/CashSessionPanel";
import { CashMovementsTable } from "../components/cash/CashMovementsTable";
import { CashMovementDialog } from "../components/cash/CashMovementDialog";
import { CashReportsPanel } from "../components/cash/CashReportsPanel";
import { CashSessionsHistory } from "../components/cash/CashSessionsHistory";
import { CashCloseReportDialog } from "../components/cash/CashCloseReportDialog";
import { TPACloseInputs } from "../components/cash/TPACloseInputs";

const CashRegisterPage = () => {
  const { token, user } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [report, setReport] = useState(null);
  const [movementType, setMovementType] = useState(null);
  const [openingBalance, setOpeningBalance] = useState("");
  const [openingNotes, setOpeningNotes] = useState("");
  const [closingBalance, setClosingBalance] = useState("");
  const [closingNotes, setClosingNotes] = useState("");
  const [tpaCounts, setTpaCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const canDelete = ["admin", "manager", "director"].includes(user?.role);

  const fetchAll = useCallback(async () => {
    try {
      const [cur, list, set] = await Promise.all([
        axios.get(`${API_URL}/cash/current`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/cash/sessions?limit=30`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/settings`, { headers, withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setSession(cur.data || null);
      setSessions(list.data || []);
      setSettings(set.data || {});
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error("Erro ao carregar caixa");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 20000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const openSession = async () => {
    const base = toBaseCurrency(openingBalance);
    if (isNaN(base) || base < 0) return toast.error("Insira um fundo inicial válido");
    setBusy(true);
    try {
      await axios.post(`${API_URL}/cash/open`, { opening_balance: base, notes: openingNotes || null }, { headers, withCredentials: true });
      toast.success("Caixa aberta — as vendas passam a ser registadas automaticamente");
      setOpenModal(false); setOpeningBalance(""); setOpeningNotes("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao abrir caixa"); } finally { setBusy(false); }
  };

  const closeSession = async () => {
    const base = toBaseCurrency(closingBalance);
    if (isNaN(base) || base < 0) return toast.error("Insira o valor contado");
    const tpaList = (session?.tpa_expected || []).filter((t) => t.tpa_id);
    const missing = tpaList.find((t) => tpaCounts[t.tpa_id] === undefined || tpaCounts[t.tpa_id] === "" || isNaN(toBaseCurrency(tpaCounts[t.tpa_id])));
    if (missing) return toast.error(`Indique o valor de fecho do ${missing.name}`);
    setBusy(true);
    try {
      const tpa_counts = tpaList.map((t) => ({ tpa_id: t.tpa_id, amount: toBaseCurrency(tpaCounts[t.tpa_id]) }));
      const r = await axios.post(`${API_URL}/cash/close`, { closing_balance: base, notes: closingNotes || null, tpa_counts }, { headers, withCredentials: true });
      const diff = r.data.difference || 0;
      const tpaDiff = r.data.tpa_difference;
      toast.success(`Caixa fechada. Diferença numerário: ${diff > 0 ? "+" : ""}${formatCurrency(diff)}${tpaDiff != null && (r.data.tpa_closings || []).length ? ` · TPA: ${tpaDiff > 0 ? "+" : ""}${formatCurrency(tpaDiff)}` : ""}`);
      setCloseModal(false); setClosingBalance(""); setClosingNotes(""); setTpaCounts({});
      setReport({ sessionId: r.data.session_id, justClosed: true });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao fechar caixa"); } finally { setBusy(false); }
  };

  const addMovement = async (payload) => {
    try {
      await axios.post(`${API_URL}/cash/movements`, payload, { headers, withCredentials: true });
      toast.success(payload.type === "entry" ? "Entrada registada" : "Saída registada");
      fetchAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registar movimento");
      return false;
    }
  };

  const deleteMovement = async (m) => {
    try {
      await axios.delete(`${API_URL}/cash/movements/${m.movement_id}`, { headers, withCredentials: true });
      toast.success("Movimento anulado");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao anular"); }
  };

  const printSession = (s) => setReport({ sessionId: s.session_id, justClosed: false });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const expectedDiff = closingBalance !== "" && session ? toBaseCurrency(closingBalance) - session.expected_cash : null;

  return (
    <div className="space-y-6" data-testid="cash-register-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Caixa</h1>
        <p className="text-muted-foreground">Abertura e fecho, entradas e saídas, e relatórios diários, semanais e mensais — vendas registadas automaticamente pelo sistema</p>
      </div>

      <CashSessionPanel session={session} onOpen={() => setOpenModal(true)} onClose={() => setCloseModal(true)} onEntry={() => setMovementType("entry")} onExit={() => setMovementType("exit")} onPrint={() => session && printSession(session)} />
      {session && <CashMovementsTable movements={session.movements || []} canDelete={canDelete} onDelete={deleteMovement} />}
      <CashReportsPanel headers={headers} settings={settings} refreshKey={refreshKey} />
      <CashSessionsHistory sessions={sessions} onPrint={printSession} />

      <CashMovementDialog type={movementType || "entry"} open={!!movementType} onClose={() => setMovementType(null)} onSubmit={addMovement} expectedCash={session?.expected_cash} />
      <CashCloseReportDialog sessionId={report?.sessionId || null} justClosed={!!report?.justClosed} onOpenChange={(o) => !o && setReport(null)} headers={headers} />

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="max-w-sm" data-testid="open-cash-modal">
          <DialogHeader><DialogTitle>Abrir Caixa</DialogTitle><DialogDescription>Conte o dinheiro em caixa e indique o fundo inicial (trocos).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Fundo inicial ({getCurrencySymbol()}) *</Label><Input type="number" step="0.01" min="0" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} className="font-mono" autoFocus data-testid="opening-balance-input" /></div>
            <div className="space-y-1"><Label>Notas</Label><Textarea rows={2} value={openingNotes} onChange={(e) => setOpeningNotes(e.target.value)} placeholder="Opcional" data-testid="opening-notes-input" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpenModal(false)}>Cancelar</Button><Button onClick={openSession} disabled={busy} data-testid="confirm-open-cash-btn">Abrir Caixa</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeModal} onOpenChange={setCloseModal}>
        <DialogContent className="max-w-md" data-testid="close-cash-modal">
          <DialogHeader><DialogTitle>Fechar Caixa</DialogTitle><DialogDescription>Conte o numerário em caixa{session?.tpa_expected?.length ? " e introduza o total do fecho de cada TPA" : ""}. O relatório de fecho (produtos vendidos, stock e pagamentos) aparece no ecrã com opções de imprimir e transferir PDF.</DialogDescription></DialogHeader>
          {session && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Fundo inicial</span><span className="font-mono">{formatCurrency(session.opening_balance)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">+ Vendas numerário</span><span className="font-mono">{formatCurrency(session.total_cash)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">+ Entradas / − Saídas (numerário)</span><span className="font-mono">{formatCurrency(session.total_cash_entries)} / {session.total_cash_exits ? "-" : ""}{formatCurrency(session.total_cash_exits)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1"><span>Esperado em caixa</span><span className="font-mono" data-testid="close-expected-cash">{formatCurrency(session.expected_cash)}</span></div>
                <div className="flex justify-between text-xs pt-1 border-t"><span className="text-muted-foreground">Vendas Multicaixa (não entram no numerário)</span><span className="font-mono" data-testid="close-total-card">{formatCurrency(session.total_card)}</span></div>
              </div>
              <div className="space-y-1"><Label>Numerário contado ({getCurrencySymbol()}) *</Label><Input type="number" step="0.01" min="0" value={closingBalance} onChange={(e) => setClosingBalance(e.target.value)} className="font-mono" autoFocus data-testid="closing-balance-input" /></div>
              {expectedDiff !== null && !isNaN(expectedDiff) && (
                <p className={`text-sm font-semibold ${expectedDiff < 0 ? "text-red-600" : expectedDiff > 0 ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="close-difference-preview">
                  Diferença: {expectedDiff > 0 ? "+" : ""}{formatCurrency(expectedDiff)} {expectedDiff === 0 ? "· caixa certa" : expectedDiff < 0 ? "· falta" : "· sobra"}
                </p>
              )}
              <TPACloseInputs tpas={session.tpa_expected || []} values={tpaCounts} onChange={setTpaCounts} />
              <div className="space-y-1"><Label>Notas de fecho</Label><Textarea rows={2} value={closingNotes} onChange={(e) => setClosingNotes(e.target.value)} placeholder="Justificação de diferenças, observações…" data-testid="closing-notes-input" /></div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setCloseModal(false)}>Cancelar</Button><Button variant="destructive" onClick={closeSession} disabled={busy} data-testid="confirm-close-cash-btn">Fechar Caixa e Gerar Relatório</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashRegisterPage;
